import { Injectable } from '@angular/core';
import { Nullable } from 'src/core/common/domain/types/types';
import { LocalFileStore } from 'src/core/file-download/domain/local-file-store';
import { FilesystemService } from 'src/core/filesystem/domain/filesystem.service';
import { Storage } from 'src/core/storage/domain/storage';

type CordovaCachedFileMetadata = {
  id: string;
  folder: string;
  filename: string;
  updatedAt: number;
};

@Injectable({
  providedIn: 'root',
})
export class CordovaLocalFileStore extends LocalFileStore {
  private readonly metadataKeyPrefix = 'local_file_store_meta:';

  constructor(private readonly filesystemService: FilesystemService, private readonly storage: Storage) {
    super();
  }

  async saveBlob(folder: string, filename: string, blob: Blob): Promise<void> {
    await this.filesystemService.writeBlobToFolder(folder, filename, blob);
    await this.saveMetadata(folder, filename);
  }

  async fileExists(folder: string, filename: string): Promise<boolean> {
    const exists = await this.filesystemService.fileExists(folder, filename);
    if (!exists) {
      await this.storage.remove(this.buildMetadataKey(folder, filename));
    }

    return exists;
  }

  async resolveLocalUrl(folder: string, filename: string): Promise<Nullable<string>> {
    const localUrl = await this.filesystemService.resolveExistingFilePath(folder, filename);
    if (!localUrl) {
      await this.storage.remove(this.buildMetadataKey(folder, filename));
    }

    return localUrl;
  }

  releaseLocalUrl(_folder: string, _filename: string): void {
    // no-op: Cordova filesystem URLs are stable and do not need runtime release.
  }

  async buildUniqueFilename(folder: string, filename: string): Promise<string> {
    return await this.filesystemService.buildUniqueFilename(folder, filename);
  }

  async pruneFilesOlderThan(cutoffTimestamp: number): Promise<number> {
    const allKeys = await this.storage.keys();
    const metadataKeys = allKeys.filter((key) => key.startsWith(this.metadataKeyPrefix));
    let removedCount = 0;

    for (const metadataKey of metadataKeys) {
      const metadata = await this.storage.get(metadataKey);
      if (!this.isMetadataRecord(metadata)) {
        continue;
      }

      const shouldDelete = metadata.updatedAt < cutoffTimestamp;
      if (!shouldDelete) {
        continue;
      }

      await this.filesystemService.deleteFile(metadata.folder, metadata.filename);
      await this.storage.remove(metadataKey);
      removedCount += 1;
    }

    return removedCount;
  }

  async clearCachedFiles(): Promise<number> {
    const allKeys = await this.storage.keys();
    const metadataKeys = allKeys.filter((key) => key.startsWith(this.metadataKeyPrefix));
    let removedCount = 0;

    for (const metadataKey of metadataKeys) {
      const metadata = await this.storage.get(metadataKey);
      if (this.isMetadataRecord(metadata)) {
        await this.filesystemService.deleteFile(metadata.folder, metadata.filename);
      }

      await this.storage.remove(metadataKey);
      removedCount += 1;
    }

    return removedCount;
  }

  private async saveMetadata(folder: string, filename: string): Promise<void> {
    const metadata: CordovaCachedFileMetadata = {
      id: this.buildRecordId(folder, filename),
      folder,
      filename,
      updatedAt: Date.now(),
    };

    await this.storage.set(this.buildMetadataKey(folder, filename), metadata);
  }

  private isMetadataRecord(value: unknown): value is CordovaCachedFileMetadata {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const maybeRecord = value as Partial<CordovaCachedFileMetadata>;
    return (
      typeof maybeRecord.id === 'string' &&
      typeof maybeRecord.folder === 'string' &&
      typeof maybeRecord.filename === 'string' &&
      typeof maybeRecord.updatedAt === 'number'
    );
  }

  private buildMetadataKey(folder: string, filename: string): string {
    return `${this.metadataKeyPrefix}${this.buildRecordId(folder, filename)}`;
  }

  private buildRecordId(folder: string, filename: string): string {
    return `${folder}/${filename}`;
  }
}
